import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { Button, H3, InputLabel, TextArea, TextInput } from '@components/Common';
import OperationLoader from '@components/Common/Loaders/OperationLoader';
import { SidebarButtons, SideForm } from '@components/Common/SideBarLayout';
import Drivers from './Drivers';
import Vehicles from './Vehicles';

// TanStack Query hooks
import { useTerminalsQuery } from '@api/queries/terminals';
import {
  useCreateTransportationCompanyMutation,
  useUpdateTransportationCompanyMutation,
  useDeleteTransportationCompanyMutation,
  TransportationCompany,
  TransportationCompanyDriver,
  TransportationCompanyVehicle
} from '@api/queries/transportationCompanies';

const RemoveButton = styled(Button)`
  margin-right: 15px;
`;

interface Props {
  transportationCompanyToEdit?: TransportationCompany | null;
  onClose: () => void;
}

const defaultTransportationCompany: TransportationCompany = {
  id: null,
  name: '',
  address: '',
  email: '',
  notes: '',
  drivers: [],
  vehicles: [],
  isDeleted: false,
  created: null,
  modified: null
};

const TransportationCompanyEditor: React.FC<Props> = ({ transportationCompanyToEdit, onClose }) => {
  const { t } = useTranslation();

  // Local form state
  const [driverEditorItem, setDriverEditorItem] = useState<TransportationCompanyDriver | null>(null);
  const [vehicleEditorItem, setVehicleEditorItem] = useState<TransportationCompanyVehicle | null>(null);
  const [transportationCompany, setTransportationCompany] = useState<TransportationCompany>(
    transportationCompanyToEdit || { ...defaultTransportationCompany }
  );

  // TanStack Query for reference data
  const { data: terminals = [], isLoading: isLoadingTerminals } = useTerminalsQuery();

  // TanStack Query mutations
  const createMutation = useCreateTransportationCompanyMutation();
  const updateMutation = useUpdateTransportationCompanyMutation();
  const deleteMutation = useDeleteTransportationCompanyMutation();

  const isLoading = isLoadingTerminals;
  const isSaving = createMutation.isPending || updateMutation.isPending || deleteMutation.isPending;

  const { name, address, email, notes, isDeleted, vehicles, drivers } = transportationCompany;
  const isEdit = transportationCompanyToEdit !== null;
  const isValid = name.trim().length > 0;
  const isSaveDisabled = isSaving || !isValid || isDeleted || !!driverEditorItem || !!vehicleEditorItem;

  const handleTextInputChanged = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setTransportationCompany({
      ...transportationCompany,
      [e.target.name]: e.target.value
    });
  };

  const handleFieldChanged = (fieldName: keyof TransportationCompany, value: any) => {
    setTransportationCompany({
      ...transportationCompany,
      [fieldName]: value
    });
  };

  const handleSubmit = (e: React.FormEvent<any>) => {
    e.preventDefault();

    if (isEdit) {
      updateMutation.mutate(transportationCompany, { onSuccess: () => onClose() });
    } else {
      createMutation.mutate(transportationCompany, { onSuccess: () => onClose() });
    }
  };

  const handleRemove = () => {
    if (confirm(t('transportationCompanies.confirm_delete_company'))) {
      deleteMutation.mutate(transportationCompany, { onSuccess: () => onClose() });
    }
  };

  return (
    <SideForm isLoading={isLoading} onEscape={onClose} onSubmit={handleSubmit}>
      {isEdit ? (
        <H3>
          {t('editing')}: <i>{transportationCompanyToEdit?.name}</i>
        </H3>
      ) : (
        <H3>
          {t('add_new')} {t('transportationCompanies.transportation_company').toLowerCase()}
        </H3>
      )}
      <div>
        <InputLabel htmlFor="name">{t('transportationCompanies.name')} *</InputLabel>
        <TextInput id="name" name="name" type="text" value={name} onChange={handleTextInputChanged} autoFocus />
      </div>
      <div>
        <InputLabel htmlFor="address">{t('transportationCompanies.address')}</InputLabel>
        <TextInput id="address" name="address" type="text" value={address} onChange={handleTextInputChanged} />
      </div>
      <div>
        <InputLabel htmlFor="email">{t('transportationCompanies.email')}</InputLabel>
        <TextInput id="email" name="email" type="text" value={email} onChange={handleTextInputChanged} />
      </div>
      <InputLabel htmlFor="notes">{t('transportationCompanies.notes')}</InputLabel>
      <div>
        <TextArea id="notes" name="notes" value={notes} onChange={handleTextInputChanged} />
      </div>
      <Vehicles
        values={vehicles}
        onChange={newValues => handleFieldChanged('vehicles', newValues)}
        editorItem={vehicleEditorItem}
        onSetEditorItem={setVehicleEditorItem}
      />
      <Drivers
        terminals={terminals}
        values={drivers}
        onChange={newValues => handleFieldChanged('drivers', newValues)}
        editorItem={driverEditorItem}
        onSetEditorItem={setDriverEditorItem}
      />
      <SidebarButtons>
        <Button type="button" secondary={true} disabled={isSaving} onClick={onClose}>
          {t('cancel')}
        </Button>
        <div />
        {isSaving && <OperationLoader title={t('saving') + '...'} />}
        <RemoveButton type="button" danger={true} disabled={isSaving || isDeleted} onClick={handleRemove}>
          {t('delete')}
        </RemoveButton>
        <Button type="submit" tertiary={true} disabled={isSaveDisabled}>
          {t('save')}
        </Button>
      </SidebarButtons>
    </SideForm>
  );
};

export default TransportationCompanyEditor;
